/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import HeadTittle from '../../../components/HeadTittle';
import DivContainerBody from '../../../components/DivContainerBody';
import React, { useCallback } from 'react';
import ButtonAction from '../../../components/buttons/ButtonAction';
import {
    EditIcon,
    EmailSubsectionChip,
    EyeIcon,
    IconAcceptedCard,
    IconCircleHyphen,
    IconToleranceTitleClock,
    OutlinedCalendarIcon,
    PersonOutlinedChip,
    PhoneLight,
    SecurityIcon,
} from '../../../components/buttons/IconButtons';
import SettingConfigApp from './Functionality/SettingConfigApp';
import { permissionObj } from '../../../Helpers/permission';
import CustomCard from '../../../components/CustomCard';
import { DATE_TIME_FORMAT_NAME_MODEL, EMPTY_VALUES, getDateFormat } from '../../../Apis/functions';
import CustomExpandableTable from '../../../components/table/CustomExpandableTable';
import CustomTooltip from '../../../components/CustomTooltip';
/**
 *
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend Developer - IBL
 * @Email: sgarcia@ibl.mx
 */
const SettingConfig = () => {
    const { t, DEV, permission, handleUpdate, columns, handleRowExpand, rows, settingConfig } = SettingConfigApp();

    // Stable reference so CustomExpandableTable (which renders this via
    // React.createElement) does not remount the expanded row on every keystroke,
    // which would make the text inputs lose focus.
    const renderExpandedRow = useCallback(({ row }) => row.content(), []);

    return (
        <>
            <div className='ml-2'>
                <HeadTittle name={'system_config'}>
                    {(DEV || permission.includes(permissionObj.WEB_SYSTEM_CONFIG_UPDATE)) && (
                        <ButtonAction
                            id={'btn_sc_update'}
                            name='update'
                            className={`btn-icon-edit`}
                            Icon={EditIcon}
                            onClick={handleUpdate}
                        />
                    )}
                </HeadTittle>
                <DivContainerBody>
                    <div className='row w-100 m-0 mt-3'>
                        <div className='col-sm-12 col-md-5 col-lg-5 px-0 pr-3'>
                            <CustomCard
                                classes={'w-100 h-100'}
                                classCard={'card-border-8px border-0 h-100 '}
                                classContent={'p-0 m-0 border-full-shades-300 border-radius-8px'}
                            >
                                <div className='d-flex align-items-center gap-2 m-3'>
                                    <EyeIcon className='h-24px w-24px icon-color-general-950' />
                                    <span className='ibl-font-w-600 fs-16px font-color-general-950'>
                                        {t('preview')}
                                    </span>
                                </div>

                                <div className='border-bottom-shades-300 my-3' />

                                <p className='font-color-general-950 fs-14px ml-2 ibl-font-w-600 mt-3 ml-3'>
                                    {t('current_configuration')}
                                </p>

                                <div className='d-flex flex-column gap-2 w-100 p-3'>
                                    <div className='p-3 border-full-shades-150 background-shades-50 border-radius-8px'>
                                        <div className='d-flex align-items-center justify-content-between w-100'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <SecurityIcon className='icon-color-shades-600 w-24px h-24px' />
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {t('otp_verification')}
                                                </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-2'>
                                                {settingConfig?.enableOtp ? (
                                                    <IconAcceptedCard className='icon-color-success-600 w-24px h-24px' />
                                                ) : (
                                                    <IconCircleHyphen className='icon-color-shades-600 w-24px h-24px' />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='p-3 border-full-shades-150 background-shades-50 border-radius-8px'>
                                        <div className='d-flex align-items-center justify-content-between w-100'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <PhoneLight className='icon-color-shades-600 w-24px h-24px' />
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {t('mobile_app')}
                                                </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-2'>
                                                {settingConfig?.hasApplication ? (
                                                    <IconAcceptedCard className='icon-color-success-600 w-24px h-24px' />
                                                ) : (
                                                    <IconCircleHyphen className='icon-color-shades-600 w-24px h-24px' />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='p-3 border-full-shades-150 background-shades-50 border-radius-8px'>
                                        <div className='d-flex align-items-center justify-content-between w-100'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <IconToleranceTitleClock className='icon-color-shades-600 w-24px h-24px' />
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {t('periodic_access_tolerance')}
                                                </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-1'>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {settingConfig?.accessTolerancePeriodicMin ?? 0}
                                                </span>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-12px text-uppercase'>
                                                    {t('min')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='p-3 border-full-shades-150 background-shades-50 border-radius-8px'>
                                        <div className='d-flex align-items-center justify-content-between w-100'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <IconToleranceTitleClock className='icon-color-shades-600 w-24px h-24px' />
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {t('one_time_access_tolerance')}
                                                </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-1'>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {settingConfig?.accessToleranceJustOnceMin ?? 0}
                                                </span>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-12px text-uppercase'>
                                                    {t('min')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='p-3 border-full-shades-150 background-shades-50 border-radius-8px'>
                                        <div className='d-flex align-items-center justify-content-between w-100'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <EmailSubsectionChip className='icon-color-shades-600 w-24px h-24px' />
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {t('validation_reminder_send_time')}
                                                </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-1'>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-14px'>
                                                    {settingConfig?.resendInvitationHour ?? 0}
                                                </span>
                                                <span className='font-color-shades-600 ibl-font-w-700 fs-12px text-uppercase'>
                                                    {t('hour')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='border-bottom-shades-300 my-2 w-100' />

                                    <p className='font-color-general-950 fs-14px ibl-font-w-600'>
                                        {t('last_modified')}
                                    </p>

                                    <div className='d-flex flex-column gap-2 w-100'>
                                        <div className='d-flex justify-content-start align-items-center'>
                                            <OutlinedCalendarIcon className='icon-color-shades-800 h-16px w-16px' />

                                            <span className='font-color-general-950 ibl-font-w-400 fs-14px ml-2'>
                                                <CustomTooltip
                                                    text={settingConfig?.lastModifiedByFullName || EMPTY_VALUES.DEFAULT}
                                                    classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                                                    showIfTruncated={true}
                                                />
                                            </span>
                                        </div>

                                        <div className='d-flex justify-content-start align-items-center'>
                                            <PersonOutlinedChip className='icon-color-shades-800 h-16px w-16px' />

                                            <span className='font-color-general-950 ibl-font-w-400 fs-14px ml-2'>
                                                {getDateFormat(
                                                    settingConfig?.lastModifiedAt,
                                                    false,
                                                    DATE_TIME_FORMAT_NAME_MODEL.dayWithShortMonth,
                                                ) || EMPTY_VALUES.DEFAULT}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </CustomCard>
                        </div>

                        <div className='col-sm-12 col-md-7 col-lg-7 px-0 ps-lg-3 mt-3 mt-lg-0'>
                            <CustomExpandableTable
                                columns={columns}
                                rows={rows}
                                renderExpandedRow={renderExpandedRow}
                                onRowExpand={handleRowExpand}
                                showHeader={false}
                                tooltip={false}
                                withoutHeight={true}
                                checkbox={false}
                                classNameRenderContainer='border-full-shades-200 background-shades-50'
                            />
                        </div>
                    </div>
                </DivContainerBody>
            </div>
        </>
    );
};

export default SettingConfig;
